import type { CodexEntry } from "@lancer-vtt/domain";
import { useLayoutEffect, useState } from "react";
import { flushSync } from "react-dom";
import { prefersReducedMotion } from "../../../../lib/prefersReducedMotion";

/** The class on `<html>` while a cleared backdrop fades out (`backdrop.css` times the fade). */
const FADING_CLASS = "backdrop-clearing";

/**
 * The backdrop the stage shows, trailing the pinned one (`live`). Clearing it fades the whole screen
 * from the planet to the plain GMS desk, scenic chrome included, through a view transition: the
 * browser snapshots the planet, swaps the DOM, and cross-fades. Pinning and switching worlds show at
 * once. With no view transitions, or under reduced motion, the clear is instant too.
 */
export function useBackdropFade(live: CodexEntry | null): CodexEntry | null {
  const [shown, setShown] = useState(live);
  useLayoutEffect(() => {
    if (live === shown) return;
    if (live !== null || shown === null || prefersReducedMotion() || typeof document.startViewTransition !== "function") {
      setShown(live);
      return;
    }
    const root = document.documentElement;
    root.classList.add(FADING_CLASS);
    const fade = document.startViewTransition(() => flushSync(() => setShown(null)));
    void fade.finished.finally(() => root.classList.remove(FADING_CLASS));
  }, [live, shown]);
  return shown;
}
